/**
 * Лейбл налогового номера.
 *
 * Россия — «ИНН», Казахстан — «БИН/ИИН» (БИН — у юрлиц,
 * ИИН — у ИП/физлиц; на практике на одной форме можем не знать,
 * кто пришёл, поэтому показываем оба варианта одной подписью).
 *
 * Используем по всему проекту (форма редактирования юр.лица,
 * карточка компании в списке/модалке, шапки таблиц, тексты
 * валидаций) — раньше «ИНН» был хардкодом и казахстанские
 * пользователи видели чужой формат на каждом экране.
 */

export const RU_TAX_LABEL = 'ИНН';
export const KZ_TAX_LABEL = 'БИН/ИИН';

export type TaxLabel = typeof RU_TAX_LABEL | typeof KZ_TAX_LABEL;

/** По 2-буквенному коду страны юрлица. */
export const getTaxLabel = (country?: string | null): TaxLabel =>
  (country || '').toLowerCase() === 'kz' ? KZ_TAX_LABEL : RU_TAX_LABEL;

/**
 * Удобная обёртка для объекта юр.лица: достаёт `country` либо из
 * самого LE (бэк-поле), либо из его юр.адреса (актуально на ранних
 * стадиях регистрации, когда `country` ещё не приехал, но
 * `addresses[0].country` уже проставлен из инвайта).
 */
export const getTaxLabelForLegalEntity = (
  le:
    | {
        country?: string | null;
        addresses?: Array<{ country?: string | null; type?: string | null }>;
      }
    | null
    | undefined
): TaxLabel => {
  if (!le) return RU_TAX_LABEL;
  if (le.country) return getTaxLabel(le.country);
  const legalAddress = (le.addresses ?? []).find((a) => a?.type === 'legal');
  return getTaxLabel(legalAddress?.country);
};

/**
 * Для случаев, когда у нас есть только Company (а не один конкретный LE) —
 * берём первое юрлицо. У одной компании в системе у юрлиц одна страна
 * (см. `RegisterCompanySerializer.create` на бэке: country приходит из
 * `partial_data.codeAlfa3` инвайта и одинаков на всю компанию).
 */
export const getTaxLabelForCompany = (
  company:
    | {
        legal_entities?: Array<{
          country?: string | null;
          addresses?: Array<{ country?: string | null; type?: string | null }>;
        }> | null;
      }
    | null
    | undefined
): TaxLabel => {
  const first = company?.legal_entities?.[0];
  return getTaxLabelForLegalEntity(first);
};
